import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Tag, MapPin, Percent, Image as ImageIcon, X, Sparkles, CheckCircle, Search, ChevronDown, Clock, Store, Calendar, Users, Instagram, Phone } from "lucide-react";
// CategoryNav removed
import { EVENTS } from "./Events";
import { useData } from '@happyspace/sdk/data';
import { usePreviewSafeData } from "../hooks/usePreviewSafeData";
import { useGooglePlaces } from "../hooks/useGooglePlaces";
import { SHOPPING_STORES } from "./Shopping";
import { GROCERY_STORES } from "./Grocery";
import { VENUES } from "./Venues";
import { TEMPLES } from "./Temples";
import { MOVIES_DATA } from "./Movies";
import { DECORATORS } from "./Decorators";
import { MAKEUP_ARTISTS } from "./MakeupHair";
import { SALON_SPA_STORES } from "./SalonSpa";
import { KIDS_ENTRIES } from "./Kids";
// Food Trucks & Hookah Lounges now merged into Restaurants page
import { PERFORMING_ARTISTS_DATA } from "./PerformingArtists";
import { DJS } from "./DJs";
import { DESSERTS_DATA } from "./Desserts";
import { MAKEUP_STORES } from "./Makeup";
import { PHOTO_VIDEO_ENTRIES } from "./PhotoVideo";
import NewsTicker from './NewsTicker';
import { DFW_LOCATIONS_WITH_OTHER, filterLocationsByPrefix } from '../data/locations';

const CATEGORIES = ["All Categories", "Restaurants", "Events", "Concerts/Events", "Temples", "Shopping", "Grocery", "Movies", "Party Venues", "Event Management", "Influencers"];
const LOCATIONS = ["Addison", "Allen", "Anna", "Arlington", "Aubrey", "Carrollton", "Cedar Hill", "Coppell", "Dallas", "Denton", "DeSoto", "Duncanville", "Farmers Branch", "Flower Mound", "Fort Worth", "Frisco", "Garland", "Grand Prairie", "Hutchins", "Irving", "Lewisville", "Mansfield", "McKinney", "Mesquite", "Midlothian", "Plano", "Richardson", "Rockwall", "Rowlett", "Terrell", "Other"];
const CITIES = ["All Cities", "Addison", "Allen", "Anna", "Arlington", "Carrollton", "Cedar Hill", "Coppell", "Dallas", "Denton", "DeSoto", "Duncanville", "Farmers Branch", "Flower Mound", "Fort Worth", "Frisco", "Garland", "Grand Prairie", "Hutchins", "Irving", "Lewisville", "Mansfield", "McKinney", "Mesquite", "Midlothian", "Plano", "Richardson", "Rockwall", "Rowlett", "Terrell"];
const COMMUNITIES = ["All Communities", "Telugu", "Tamil", "Gujarati", "Punjabi", "Hindi", "Marathi", "Bengali", "Kannada", "Malayali", "Sikh", "Jain", "Muslim"];
const DEAL_TYPES = [
  { key: "all", label: "All Deals" },
  { key: "business", label: "Business Deals" },
  { key: "event", label: "Event & Concert Deals" },
  { key: "community", label: "Community Deals" },
];
const DISCOUNT_TYPES = ["Percentage Off", "Fixed Amount Off", "BOGO", "Free Item", "Bundle Deal"];

const inputClass =
  "w-full border rounded-xl px-3.5 py-2.5 text-[13px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:border-orange-400 placeholder:text-gray-400";

const AVATAR_COLORS = ["#f97316", "#2563eb", "#16a34a", "#7c3aed", "#db2777", "#0891b2"];
function initialsOf(name: string) {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "D") + (p[1]?.[0] || "")).toUpperCase();
}
function colorOf(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function fmtDate(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateLong(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// Downscale + compress before turning into a data URL. The platform's API
// rejects large JSON bodies, so sending a raw multi-MB photo makes the whole
// submission fail to save. Matching the admin uploader (maxDim 1000, q0.82),
// which is proven to save on the platform, keeps the payload small.
function fileToDataUrl(file: File, maxDim = 1000, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't a valid image."));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(reader.result as string); // fallback: original data URL
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const out =
          file.type === "image/png" && scale === 1
            ? canvas.toDataURL("image/png")
            : canvas.toDataURL("image/jpeg", quality);
        resolve(out);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

function formatDiscountValue(type: string, value: string): string {
  if (!value) return "";
  if (type === "Percentage Off") return `${value}% OFF`;
  if (type === "Fixed Amount Off") return `$${value} OFF`;
  if (type === "BOGO") return "Buy 1 Get 1";
  if (type === "Free Item") return "Free Item";
  if (type === "Bundle Deal") return "Bundle Deal";
  return value;
}

function DealCard({ deal, index }: { deal: any; index: number }) {
  const name = deal.submittedName || "Anonymous";
  const color = deal.isAnonymous ? "#94a3b8" : colorOf(name);
  const discountLabel = formatDiscountValue(deal.discountType, deal.discountValue);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 6) * 0.05 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0" style={{ background: color }}>
          {deal.isAnonymous ? "D" : initialsOf(name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-gray-900">{name}</span>
            {(deal.createdAt || deal.submittedAt) && <span className="text-[11px] text-gray-400">{fmtDate(deal.createdAt || deal.submittedAt)}</span>}
          </div>
          {deal.businessName && (
            <p className="text-[13px] font-semibold text-[#082f6d] mt-0.5">{deal.businessName}</p>
          )}
          {deal.dealTitle && (
            <p className="text-[14px] font-bold text-gray-900 mt-1">{deal.dealTitle}</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {discountLabel && (
              <span className="text-[10.5px] font-bold text-white bg-[#51ba4f] rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                <Percent className="w-3 h-3" /> {discountLabel}
              </span>
            )}
            {deal.category && <span className="text-[10.5px] font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5">{deal.category}</span>}
            {deal.location && (
              <span className="text-[10.5px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {deal.location}
              </span>
            )}
            {deal.expiryDate && (
              <span className="text-[10.5px] font-semibold text-red-700 bg-red-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Expires {fmtDate(deal.expiryDate)}
              </span>
            )}
          </div>
          {deal.description && <p className="text-[13px] text-gray-600 leading-relaxed mt-2.5">{deal.description}</p>}
          {deal.socialMediaLink && (
            <a
              href={deal.socialMediaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#c026a3] hover:text-[#a21c88] mt-2.5 transition-colors"
            >
              <Instagram className="w-3.5 h-3.5" /> Contact on Instagram
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function FeaturedDealCard({ deal, index, imageClassName = "w-full h-auto max-h-[420px] object-contain" }: { deal: any; index: number; imageClassName?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 6) * 0.05 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      {deal.imageUrl && (
        <div className="w-full bg-gray-50">
          <img src={deal.imageUrl} alt={deal.dealTitle || deal.businessName} className={imageClassName} />
        </div>
      )}
      <div className="p-5">
        {deal.businessName && (
          <p className="text-[13px] font-semibold text-[#082f6d]">{deal.businessName}</p>
        )}
        {deal.dealTitle && (
          <p className="text-[16px] font-bold text-gray-900 mt-1">{deal.dealTitle}</p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {deal.category && <span className="text-[10.5px] font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5">{deal.category}</span>}
          {deal.city && (
            <span className="text-[10.5px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {deal.city}
            </span>
          )}
          {deal.expiryDate && (
            <span className="text-[10.5px] font-semibold text-red-700 bg-red-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> Expires {fmtDate(deal.expiryDate)}
            </span>
          )}
        </div>
        {deal.description && <p className="text-[13px] text-gray-600 leading-relaxed mt-2.5">{deal.description}</p>}
        {deal.socialMediaLink && (
          <a
            href={deal.socialMediaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#c026a3] hover:text-[#a21c88] mt-3 transition-colors"
          >
            <Instagram className="w-3.5 h-3.5" /> Contact on Instagram
          </a>
        )}
      </div>
    </motion.div>
  );
}

export const FEATURED_BUSINESS_DEALS: {
  _id: string;
  businessName: string;
  category: string;
  dealTitle: string;
  description: string;
  city: string;
  dealType: string;
  socialMediaLink: string;
  imageUrl: string;
  expiryDate?: string;
}[] = [];

export default function Deals() {
  const navigate = useNavigate();
  const { insert: insertBusinessDeal } = useData("business_deals");
  const { insert: insertEventDeal } = useData("event_deals");
  const { insert: insertCommunityDeal } = useData("community_deals");
  const { rows: existingBusinesses } = usePreviewSafeData("business_listings");
  const { places: googlePlaces } = useGooglePlaces();
  // hero search/location/category filters
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("All Locations");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [cityFilter, setCityFilter] = useState("All Cities");
  const [communityFilter, setCommunityFilter] = useState("All Communities");
  const [dealType, setDealType] = useState("all");
  const [catOpen, setCatOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const locRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const communityRef = useRef<HTMLDivElement>(null);
  const dealsSectionRef = useRef<HTMLDivElement>(null);
  const [dealNameSearch, setDealNameSearch] = useState("");

  // Form's City field — searchable dropdown (matches the hero location dropdown UX)
  const [showFormCityDropdown, setShowFormCityDropdown] = useState(false);
  const [formCitySearch, setFormCitySearch] = useState("");
  const formCityDropdownRef = useRef<HTMLDivElement>(null);
  const formCityInputRef = useRef<HTMLInputElement>(null);
  const filteredFormCities = filterLocationsByPrefix(DFW_LOCATIONS_WITH_OTHER, formCitySearch);

  useEffect(() => {
    function h(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (locRef.current && !locRef.current.contains(e.target as Node)) setLocOpen(false);
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityOpen(false);
      if (communityRef.current && !communityRef.current.contains(e.target as Node)) setCommunityOpen(false);
      if (formCityDropdownRef.current && !formCityDropdownRef.current.contains(e.target as Node)) {
        setShowFormCityDropdown(false);
        setFormCitySearch("");
      }
    }
    if (catOpen || locOpen || cityOpen || communityOpen || showFormCityDropdown) {
      document.addEventListener("mousedown", h);
      return () => document.removeEventListener("mousedown", h);
    }
  }, [catOpen, locOpen, cityOpen, communityOpen, showFormCityDropdown]);

  // Auto-set form.dealType to match the active tab
  useEffect(() => {
    if (dealType === "business") {
      setForm((p) => ({ ...p, dealType: "business" }));
    } else if (dealType === "event") {
      setForm((p) => ({ ...p, dealType: "event" }));
    } else if (dealType === "community") {
      setForm((p) => ({ ...p, dealType: "community" }));
    }
  }, [dealType]);

  // form state
  const [form, setForm] = useState({ businessName: "", dealTitle: "", description: "", expiryDate: "", category: "", city: "", community: "", dealType: "", socialMediaLink: "", contactName: "", contactEmail: "", contactNumber: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justPosted, setJustPosted] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // business name search state
  const [businessSearch, setBusinessSearch] = useState("");
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<any | null>(null);
  const businessDropdownRef = useRef<HTMLDivElement>(null);

  // click-outside for business search dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (businessDropdownRef.current && !businessDropdownRef.current.contains(e.target as Node)) {
        setShowBusinessDropdown(false);
      }
    }
    if (showBusinessDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showBusinessDropdown]);

  // ── Unified cross-category search ──
  // Normalize all category arrays into a common shape: { name, category, location, source }
  function normalizeForSearch(entry: any, category: string): any {
    const name = entry.name || entry.title || entry.eventName || "";
    const location = entry.location || entry.city || entry.address || "";
    return { ...entry, _searchName: name, _searchCategory: category, _searchLocation: location };
  }

  const ALL_LISTINGS = [
    ...(existingBusinesses || []).map((b: any) => normalizeForSearch(b, b.eventCategory || "Business")),
    ...EVENTS.map((ev) => normalizeForSearch(ev, ev.category || "Events")),
    ...(googlePlaces || []).map((p: any) => normalizeForSearch(p, "Restaurants")),
    ...SHOPPING_STORES.map((s) => normalizeForSearch(s, "Shopping")),
    ...GROCERY_STORES.map((g) => normalizeForSearch(g, "Grocery")),
    ...VENUES.map((v) => normalizeForSearch(v, "Party Venues")),
    ...TEMPLES.map((t) => normalizeForSearch(t, "Temples")),
    ...MOVIES_DATA.map((m) => normalizeForSearch(m, "Movies")),
    ...DECORATORS.map((d) => normalizeForSearch(d, "Event Management")),
    ...MAKEUP_ARTISTS.map((m) => normalizeForSearch(m, "Beauty Services")),
    ...SALON_SPA_STORES.map((s) => normalizeForSearch(s, "Salon & Spa")),
    ...KIDS_ENTRIES.map((k) => normalizeForSearch(k, "Kids")),
    // Food Trucks & Hookah Lounges now included via Restaurants page
    ...PERFORMING_ARTISTS_DATA.map((l) => normalizeForSearch(l, "Performing Artists")),
    ...DJS.map((d) => normalizeForSearch(d, "DJs")),
    ...DESSERTS_DATA.map((d) => normalizeForSearch(d, "Dessert Shops")),
    ...MAKEUP_STORES.map((m) => normalizeForSearch(m, "Beauty Services")),
    ...PHOTO_VIDEO_ENTRIES.map((p) => normalizeForSearch(p, "Photo & Video")),
  ];

  const filteredListings = (() => {
    if (!businessSearch.trim()) return ALL_LISTINGS.slice(0, 15);
    const q = businessSearch.toLowerCase();
    return ALL_LISTINGS.filter((item: any) =>
      (item._searchName || "").toLowerCase().includes(q) ||
      (item._searchCategory || "").toLowerCase().includes(q) ||
      (item._searchLocation || "").toLowerCase().includes(q)
    ).slice(0, 15);
  })();

  function selectBusiness(biz: any) {
    const name = biz._searchName || biz.eventName || biz.name || "";
    const category = biz._searchCategory || biz.eventCategory || biz.category || "";
    setSelectedBusiness({ ...biz, eventName: name, eventCategory: category });
    update("businessName", name);
    if (category) update("category", category);
    setBusinessSearch("");
    setShowBusinessDropdown(false);
  }

  function clearSelectedBusiness() {
    setSelectedBusiness(null);
    update("businessName", "");
    setBusinessSearch("");
  }

  function update(field: string, value: string) {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  }

  function handleImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    setImagePreview(URL.createObjectURL(file));
  }
  function removeImage() {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImageName("");
    if (fileRef.current) fileRef.current.value = "";
  }

  function validate() {
    const e: Record<string, string> = {};
    if (!selectedBusiness) e.businessName = dealType === "event" ? "Please search and select an event from the list." : dealType === "community" ? "Please search and select a contact from the list." : "Please search and select a business from the list.";
    if (!form.dealTitle.trim()) e.dealTitle = "Deal title is required.";
    if (!form.description.trim()) e.description = "Describe the deal.";
    if (!form.category) e.category = "Choose a category.";
    if (!form.dealType) e.dealType = "Select a deal type.";
    if (dealType === "business") {
      if (!form.contactName.trim()) e.contactName = "Contact name is required.";
      if (!form.contactEmail.trim()) e.contactEmail = "Contact email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) e.contactEmail = "Enter a valid email address.";
    }
    if (dealType === "community") {
      if (!form.contactName.trim()) e.contactName = "Contact name is required.";
      if (!form.contactEmail.trim()) e.contactEmail = "Contact email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) e.contactEmail = "Enter a valid email address.";
    }
    if (dealType === "event") {
      if (!form.contactEmail.trim()) e.contactEmail = "Contact email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) e.contactEmail = "Enter a valid email address.";
    }
    if (!fileRef.current?.files?.[0]) e.image = "Please upload an image.";
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitError(null);
    setSubmitting(true);

    const snapBusinessName = selectedBusiness?.eventName?.trim() || form.businessName.trim();
    const snapDealTitle = form.dealTitle.trim();
    const snapDescription = form.description.trim();
    const snapExpiryDate = form.expiryDate || null;
    const snapCategory = form.category;
    const snapCity = form.city || null;
    const snapCommunity = form.community || null;
    const snapDealType = form.dealType || null;
    const snapSubmittedName = selectedBusiness?.eventName?.trim() || form.businessName.trim() || "Anonymous";
    const snapIsAnonymous = !snapSubmittedName || snapSubmittedName === "Anonymous";
    const snapContactName = form.contactName.trim() || null;
    const snapContactEmail = form.contactEmail.trim() || null;
    const snapContactNumber = form.contactNumber.trim() || null;
    const snapCreatedAt = new Date().toISOString();
    const imageFile = fileRef.current?.files?.[0] ?? null;

    try {
      const imageUrl = imageFile ? await fileToDataUrl(imageFile) : undefined;

      const persistEntry: Record<string, any> = {
        businessName: snapBusinessName,
        dealTitle: snapDealTitle,
        description: snapDescription,
        category: snapCategory,
        submittedName: snapSubmittedName,
        isAnonymous: snapIsAnonymous,
        imageUrl,
        source: "deal-form",
        status: "pending",
        submittedAt: snapCreatedAt,
        createdAt: snapCreatedAt,
      };
      if (snapExpiryDate) persistEntry.expiryDate = snapExpiryDate;
      if (snapCity) persistEntry.city = snapCity;
      if (snapCommunity) persistEntry.community = snapCommunity;
      if (snapDealType) persistEntry.dealType = snapDealType;
      if (form.socialMediaLink.trim()) persistEntry.socialMediaLink = form.socialMediaLink.trim();
      if (snapContactName) persistEntry.contactName = snapContactName;
      if (snapContactEmail) persistEntry.contactEmail = snapContactEmail;
      if (snapContactNumber) persistEntry.contactNumber = snapContactNumber;

      // Insert only — no read from table
      if (dealType === "event") {
        await insertEventDeal(persistEntry);
      } else if (dealType === "community") {
        await insertCommunityDeal(persistEntry);
      } else {
        await insertBusinessDeal(persistEntry);
      }

      setForm({ businessName: "", dealTitle: "", description: "", expiryDate: "", category: "", city: "", community: "", dealType: "", socialMediaLink: "", contactName: "", contactEmail: "", contactNumber: "" });
      setSelectedBusiness(null);
      setBusinessSearch("");
      removeImage();
      setJustPosted(true);
      setTimeout(() => setJustPosted(false), 4000);
    } catch (err) {
      console.error("Deals insert failed:", err);
      setSubmitError(err instanceof Error ? err.message : "Couldn't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Curated/featured deals shown on the public page (business + "all" tabs).
  // Submitted forms still write to their own tables for admin review — this
  // list is the hand-picked set actually surfaced to visitors.
  const deals: any[] = (dealType === "event" || dealType === "community")
    ? []
    : FEATURED_BUSINESS_DEALS.filter((d) => {
        if (cityFilter !== "All Cities" && d.city !== cityFilter) return false;
        if (dealNameSearch.trim()) {
          const q = dealNameSearch.trim().toLowerCase();
          if (!(d.businessName + " " + d.dealTitle).toLowerCase().includes(q)) return false;
        }
        return true;
      });

  return (
    <div className="min-h-screen bg-white">
      {/* Custom Hero with Category Dropdown */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/38c0a4b3b01c.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md p-1.5 sm:p-0" style={{ boxShadow: "0 0 40px 12px rgba(255,255,255,0.25), 0 0 80px 24px rgba(255,255,255,0.1)" }}>
              <div className="flex flex-col gap-1.5 sm:gap-0 sm:flex-row bg-transparent sm:bg-white rounded-md sm:shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2.5 sm:py-0 flex items-center bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[44px] sm:min-h-[40px]">
                  <Search className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400 mr-2 shrink-0" />
                  <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search deals…" className="w-full text-[13px] sm:text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                {/* Category Dropdown */}
                <div ref={catRef} className="relative bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[44px] sm:min-h-[40px] sm:w-[150px] shrink-0" style={{ zIndex: 99999 }}>
                  <button type="button" onClick={() => { setCatOpen(!catOpen); setLocOpen(false); }} className="w-full h-full px-3 py-2.5 sm:py-0 flex items-center gap-1.5 text-left min-h-[44px] sm:min-h-[40px]">
                    <Tag className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{categoryFilter}</span>
                    <ChevronDown className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${catOpen ? "rotate-180" : ""}`} />
                  </button>
                  {catOpen && (
                    <div className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto" style={{ zIndex: 99999, marginTop: "4px", width: "190px", maxHeight: "230px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", fontFamily: "'Poppins', sans-serif" }}>
                      {CATEGORIES.map((c) => (
                        <button key={c} type="button" onClick={() => { setCategoryFilter(c); setCatOpen(false); }} className="w-full text-left flex items-center transition-colors duration-150" style={{ padding: "8px 12px", fontSize: "13px", fontWeight: categoryFilter === c ? 700 : 500, color: categoryFilter === c ? "#f97316" : "#111827", background: categoryFilter === c ? "#fff3e8" : "transparent", height: "36px" }} onMouseEnter={(e) => { if (categoryFilter !== c) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }} onMouseLeave={(e) => { if (categoryFilter !== c) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Location Dropdown */}
                <div ref={locRef} className="relative bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[44px] sm:min-h-[40px] sm:w-[150px] shrink-0" style={{ zIndex: 99999 }}>
                  <button type="button" onClick={() => { setLocOpen(!locOpen); setCatOpen(false); }} className="w-full h-full px-3 py-2.5 sm:py-0 flex items-center gap-1.5 text-left min-h-[44px] sm:min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{location}</span>
                    <ChevronDown className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${locOpen ? "rotate-180" : ""}`} />
                  </button>
                  {locOpen && (
                    <div className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto" style={{ zIndex: 99999, marginTop: "4px", width: "190px", maxHeight: "230px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", fontFamily: "'Poppins', sans-serif" }}>
                      {["All Locations", ...LOCATIONS].map((l) => (
                        <button key={l} type="button" onClick={() => { setLocation(l); setLocOpen(false); }} className="w-full text-left flex items-center transition-colors duration-150" style={{ padding: "8px 12px", fontSize: "13px", fontWeight: location === l ? 700 : 500, color: location === l ? "#f97316" : "#111827", background: location === l ? "#fff3e8" : "transparent", height: "36px" }} onMouseEnter={(e) => { if (location !== l) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }} onMouseLeave={(e) => { if (location !== l) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => { setCatOpen(false); setLocOpen(false); }} className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[44px] sm:min-h-[40px] rounded-md sm:rounded-l-none w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" /> Search
                  </span>
                </button>
              </div>
            </div>


          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* 3 Deal Buttons */}
      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-3">
        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            onClick={() => { setDealType("business"); setTimeout(() => { dealsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80); }}
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl border-2 border-[#082f6d] bg-white hover:bg-[#082f6d] text-[#082f6d] hover:text-white text-[13px] sm:text-[14px] font-semibold transition-all duration-200"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <Store className="w-4 h-4" />
            Add Business Deal
          </motion.button>
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onClick={() => { setDealType("event"); setTimeout(() => { dealsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80); }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl border-2 border-[#51ba4f] bg-white hover:bg-[#51ba4f] text-[#51ba4f] hover:text-white text-[13px] sm:text-[14px] font-semibold transition-all duration-200"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <Calendar className="w-4 h-4" />
            Add Event Deal
          </motion.button>
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.26 }}
            onClick={() => { setDealType("community"); setTimeout(() => { dealsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80); }}
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl border-2 border-[#f59e0b] bg-white hover:bg-[#f59e0b] text-[#f59e0b] hover:text-white text-[13px] sm:text-[14px] font-semibold transition-all duration-200"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <Users className="w-4 h-4" />
            Add Community Deal
          </motion.button>
        </div>
      </div>

      {/* Deal Type Tabs */}
      <div ref={dealsSectionRef} className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex gap-1 overflow-x-auto">
          {DEAL_TYPES.map((dt) => (
            <button
              key={dt.key}
              type="button"
              onClick={() => setDealType(dt.key)}
              className="text-[13px] font-semibold px-4 py-3 border-b-2 transition-colors whitespace-nowrap"
              style={{
                color: dealType === dt.key ? "#51ba4f" : "#6b7280",
                borderColor: dealType === dt.key ? "#51ba4f" : "transparent",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {dt.label}
            </button>
          ))}
        </div>
      </div>

      <section className="bg-gray-50/50 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: deals list */}
          <div className="flex-1 min-w-0 order-2 lg:order-1">
            <p className="text-[13px] md:text-[14px] text-gray-500 font-medium mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Discover exclusive offers, event promotions, and community deals across DFW.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
              <h2 className="text-[18px] md:text-[20px] font-extrabold text-gray-900">{dealType === "business" ? "DFW Business Deals & Promotions" : dealType === "event" ? "DFW Event & Concert Deals" : dealType === "community" ? "DFW Community Deals" : "DFW Deals & Promotions"}</h2>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:w-[220px]">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={dealNameSearch}
                    onChange={(e) => setDealNameSearch(e.target.value)}
                    placeholder={dealType === "event" ? "Search by Event Name" : dealType === "business" ? "Search by Business Name" : "Search by Name"}
                    className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-[12px] text-gray-800 outline-none transition-all focus:ring-2 focus:ring-[#51ba4f]/20 focus:border-[#51ba4f] placeholder:text-gray-400"
                  />
                  {dealNameSearch && (
                    <button type="button" onClick={() => setDealNameSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {/* City filter */}
                <div ref={cityRef} className="relative" style={{ zIndex: 9999 }}>
                  <button
                    type="button"
                    onClick={() => { setCityOpen(!cityOpen); setCommunityOpen(false); }}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:border-[#51ba4f] hover:text-[#51ba4f] transition-colors"
                    style={{ color: cityFilter !== "All Cities" ? "#51ba4f" : "#374151", borderColor: cityFilter !== "All Cities" ? "#51ba4f" : "#e5e7eb", minHeight: "32px" }}
                  >
                    <MapPin className="w-3 h-3" /> {cityFilter}
                    <ChevronDown className={`w-3 h-3 transition-transform ${cityOpen ? "rotate-180" : ""}`} />
                  </button>
                  {cityOpen && (
                    <div className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto" style={{ zIndex: 9999, marginTop: "4px", width: "170px", maxHeight: "230px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", fontFamily: "'Poppins', sans-serif" }}>
                      {CITIES.map((c) => (
                        <button key={c} type="button" onClick={() => { setCityFilter(c); setCityOpen(false); }} className="w-full text-left flex items-center transition-colors duration-150" style={{ padding: "8px 12px", fontSize: "13px", fontWeight: cityFilter === c ? 700 : 500, color: cityFilter === c ? "#51ba4f" : "#111827", background: cityFilter === c ? "#f0fdf4" : "transparent", height: "36px" }} onMouseEnter={(e) => { if (cityFilter !== c) (e.currentTarget as HTMLButtonElement).style.background = "#f9fafb"; }} onMouseLeave={(e) => { if (cityFilter !== c) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-[12px] text-gray-400 shrink-0">{deals.length}</span>
              </div>
            </div>
            {deals.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {deals.map((deal, i) => <FeaturedDealCard key={deal._id || i} deal={deal} index={i} />)}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-10 text-center">
                <Sparkles className="w-7 h-7 text-amber-400 mx-auto mb-2" />
                <p className="text-[14px] font-semibold text-gray-700">No deals yet</p>
                <p className="text-[12.5px] text-gray-500 mt-1">{dealType === "all" ? "Switch to Business, Event, or Community tab to post a deal." : "Be the first to share a deal — use the form on the right."}</p>
              </div>
            )}
          </div>

          {/* RIGHT: form — only for Business, Event, and Community tabs */}
          {dealType !== "all" && (
          <aside className="w-full lg:w-[360px] shrink-0 lg:sticky lg:top-6 self-start order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 pt-5 pb-4 border-b border-gray-100">
                <Link
                  to={dealType === "event" ? "/add-event" : "/add-business"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-semibold px-4 py-2.5 rounded-md transition-colors duration-200"
                >
                  {dealType === "event" ? "Add Event" : dealType === "community" ? "Community Deal" : "Add Business"}
                </Link>
                <p className="text-[12.5px] text-gray-500 mt-3">{dealType === "event" ? "Your Event must be listed on Dallas Desi Scoop before you can add a deal. If your Event isn't listed yet, click the \"Add Event\" button above to submit your Event first" : dealType === "community" ? "Community Deals are for individuals only. Ticket transfers, sharing, giveaways etc" : "Your business must be listed on Dallas Desi Scoop before you can add a deal. If your business isn't listed yet, click the \"Add Business\" button above to submit your business first"}</p>
              </div>

              {/* Success toast pop-up — fixed at top of screen */}
              <AnimatePresence>
                {justPosted && (
                  <motion.div
                    initial={{ opacity: 0, y: -60, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -60, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className="fixed top-6 left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-3 bg-[#51ba4f] text-white rounded-2xl px-5 py-3.5 shadow-2xl shadow-green-500/30 max-w-[90vw]"
                  >
                    <CheckCircle className="w-5 h-5 shrink-0" />
                    <p className="text-[14px] font-semibold whitespace-nowrap">Deal submitted. We will review and get back in 24 hrs</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} noValidate className="p-5 space-y-4">
                {/* Business Name — searchable dropdown */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{dealType === "event" ? "Event Name" : dealType === "community" ? "Contact Name" : "Business Name"} <span className="text-red-400">*</span></label>
                  {selectedBusiness ? (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
                      <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                      <span className="text-[13px] font-semibold text-green-800 flex-1 truncate">{selectedBusiness.eventName}</span>
                      <button type="button" onClick={clearSelectedBusiness} className="text-gray-400 hover:text-red-500 transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div ref={businessDropdownRef} className="relative" style={{ zIndex: 9999 }}>
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                          type="text"
                          value={businessSearch}
                          onChange={(e) => { setBusinessSearch(e.target.value); setShowBusinessDropdown(true); }}
                          onFocus={() => setShowBusinessDropdown(true)}
                          placeholder={dealType === "event" ? "Search for an event…" : dealType === "community" ? "Search for a contact…" : "Search for a business…"}
                          className={`${inputClass} pl-9 ${errors.businessName ? "border-red-400" : "border-gray-200"}`}
                        />
                      </div>
                      {showBusinessDropdown && filteredListings.length > 0 && (
                        <div
                          className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-lg overflow-y-auto mt-1"
                          style={{ maxHeight: "220px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", zIndex: 9999 }}
                        >
                          {filteredListings.map((item: any, i: number) => (
                            <button
                              key={item._id || item.id || i}
                              type="button"
                              onClick={() => selectBusiness(item)}
                              className="w-full text-left px-3 py-2.5 hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-b-0"
                            >
                              <span className="text-[13px] font-semibold text-gray-800 block truncate">{item._searchName}</span>
                              <span className="text-[11px] text-gray-500">{item._searchCategory}{item._searchLocation ? ` · ${item._searchLocation}` : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {showBusinessDropdown && businessSearch.trim() && filteredListings.length === 0 && (
                        <div
                          className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-lg mt-1 px-3 py-3"
                          style={{ boxShadow: "0 10px 24px rgba(15,23,42,0.16)", zIndex: 9999 }}
                        >
                          <p className="text-[12px] text-red-500 font-medium">Business not found.</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">Try a different search or add the business first.</p>
                        </div>
                      )}
                    </div>
                  )}
                  {errors.businessName && <p className="text-red-500 text-[11px] mt-1">{errors.businessName}</p>}
                </div>

                {/* Deal Title */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Deal Title <span className="text-red-400">*</span></label>
                  <input type="text" value={form.dealTitle} onChange={(e) => update("dealTitle", e.target.value)} placeholder='e.g. "Happy Hour 50% Off"' className={`${inputClass} ${errors.dealTitle ? "border-red-400" : "border-gray-200"}`} />
                  {errors.dealTitle && <p className="text-red-500 text-[11px] mt-1">{errors.dealTitle}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Description <span className="text-red-400">*</span></label>
                  <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={3} placeholder="Details — what's included, any restrictions…" className={`${inputClass} resize-none ${errors.description ? "border-red-400" : "border-gray-200"}`} />
                  {errors.description && <p className="text-red-500 text-[11px] mt-1">{errors.description}</p>}
                </div>

                {/* Expiry Date */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Expiry Date</label>
                  <input type="date" value={form.expiryDate} onChange={(e) => update("expiryDate", e.target.value)} className={`${inputClass} border-gray-200`} />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Category <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <Tag className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select value={form.category} onChange={(e) => update("category", e.target.value)} className={`${inputClass} pl-9 appearance-none bg-white ${errors.category ? "border-red-400" : "border-gray-200"} ${form.category ? "text-gray-800" : "text-gray-400"}`}>
                      <option value="">Select a category</option>
                      {(dealType === "event" ? ["Concerts/Events"] : CATEGORIES).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  {errors.category && <p className="text-red-500 text-[11px] mt-1">{errors.category}</p>}
                </div>

                {/* Deal Type */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Deal Type <span className="text-red-400">*</span></label>
                  {dealType === "business" ? (
                    <div className="flex items-center gap-2 bg-[#082f6d]/5 border border-[#082f6d]/20 rounded-xl px-3 py-2.5">
                      <Store className="w-3.5 h-3.5 text-[#082f6d]" />
                      <span className="text-[13px] font-semibold text-[#082f6d]">Business Deal</span>
                      <CheckCircle className="w-3.5 h-3.5 text-[#51ba4f] ml-auto" />
                    </div>
                  ) : dealType === "event" ? (
                    <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2.5">
                      <Calendar className="w-3.5 h-3.5 text-purple-600" />
                      <span className="text-[13px] font-semibold text-purple-700">Event Deal</span>
                      <CheckCircle className="w-3.5 h-3.5 text-[#51ba4f] ml-auto" />
                    </div>
                  ) : dealType === "community" ? (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                      <Users className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-[13px] font-semibold text-amber-700">Community Deal</span>
                      <CheckCircle className="w-3.5 h-3.5 text-[#51ba4f] ml-auto" />
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Percent className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <select value={form.dealType} onChange={(e) => update("dealType", e.target.value)} className={`${inputClass} pl-9 appearance-none bg-white ${errors.dealType ? "border-red-400" : "border-gray-200"} ${form.dealType ? "text-gray-800" : "text-gray-400"}`}>
                          <option value="">Select deal type</option>
                          {DEAL_TYPES.filter(dt => dt.key !== "all").map((dt) => <option key={dt.key} value={dt.key}>{dt.label}</option>)}
                        </select>
                      </div>
                      {errors.dealType && <p className="text-red-500 text-[11px] mt-1">{errors.dealType}</p>}
                    </>
                  )}
                </div>

                {/* City */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">City</label>
                  <div className="relative" ref={formCityDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowFormCityDropdown(!showFormCityDropdown);
                        if (!showFormCityDropdown) setTimeout(() => formCityInputRef.current?.focus(), 0);
                      }}
                      className={`${inputClass} pl-9 pr-9 text-left flex items-center gap-1.5 bg-white border-gray-200`}
                    >
                      <MapPin className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <span className={form.city ? "text-gray-800" : "text-gray-400"}>
                        {form.city || "Select a city"}
                      </span>
                      <svg
                        className={"w-3 h-3 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 transition-transform " + (showFormCityDropdown ? "rotate-180" : "")}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showFormCityDropdown && (
                      <div
                        className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-lg py-1 overflow-hidden z-50"
                        style={{ marginTop: "4px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)" }}
                      >
                        <div className="px-3 py-2 border-b border-gray-100 sticky top-0 bg-white">
                          <input
                            ref={formCityInputRef}
                            type="text"
                            autoFocus
                            value={formCitySearch}
                            onChange={(e) => setFormCitySearch(e.target.value)}
                            placeholder="Type a city..."
                            className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                          />
                        </div>
                        <div className="overflow-y-auto" style={{ maxHeight: "200px" }}>
                          {filteredFormCities.length > 0 ? (
                            filteredFormCities.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => { update("city", c); setShowFormCityDropdown(false); setFormCitySearch(""); }}
                                className="w-full text-left px-3 py-2 text-[13px] transition-colors duration-150 hover:bg-orange-50"
                                style={{
                                  fontWeight: form.city === c ? 700 : 500,
                                  color: form.city === c ? "#f97316" : "#111827",
                                  background: form.city === c ? "#fff3e8" : "transparent",
                                }}
                              >
                                {c}
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-4 text-center text-[12px] text-gray-400">
                              No cities match "{formCitySearch}"
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Social Media Link */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Social Media Link</label>
                  <input type="url" value={form.socialMediaLink} onChange={(e) => update("socialMediaLink", e.target.value)} placeholder="e.g. https://instagram.com/yourbusiness" className={`${inputClass} border-gray-200`} />
                </div>

                {/* Contact Email & Phone — event deals */}
                {dealType === "event" && (
                  <>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Email <span className="text-red-400">*</span></label>
                      <input type="email" value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} placeholder="e.g. name@email.com" className={`${inputClass} ${errors.contactEmail ? "border-red-400" : "border-gray-200"}`} />
                      {errors.contactEmail && <p className="text-red-500 text-[11px] mt-1">{errors.contactEmail}</p>}
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Phone <span className="text-gray-400">(optional)</span></label>
                      <input type="tel" value={form.contactNumber} onChange={(e) => update("contactNumber", e.target.value)} placeholder="e.g. (214) 555-0123" className={`${inputClass} border-gray-200`} />
                    </div>
                  </>
                )}

                {/* Contact Name, Email & Phone — business deals */}
                {dealType === "business" && (
                  <>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Name <span className="text-red-400">*</span></label>
                      <input type="text" value={form.contactName} onChange={(e) => update("contactName", e.target.value)} placeholder="e.g. John Doe" className={`${inputClass} ${errors.contactName ? "border-red-400" : "border-gray-200"}`} />
                      {errors.contactName && <p className="text-red-500 text-[11px] mt-1">{errors.contactName}</p>}
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Email <span className="text-red-400">*</span></label>
                      <input type="email" value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} placeholder="e.g. name@email.com" className={`${inputClass} ${errors.contactEmail ? "border-red-400" : "border-gray-200"}`} />
                      {errors.contactEmail && <p className="text-red-500 text-[11px] mt-1">{errors.contactEmail}</p>}
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Phone <span className="text-gray-400">(optional)</span></label>
                      <input type="tel" value={form.contactNumber} onChange={(e) => update("contactNumber", e.target.value)} placeholder="e.g. (214) 555-0123" className={`${inputClass} border-gray-200`} />
                    </div>
                  </>
                )}

                {/* Contact Name, Email & Phone — community deals */}
                {dealType === "community" && (
                  <>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Name <span className="text-red-400">*</span></label>
                      <input type="text" value={form.contactName} onChange={(e) => update("contactName", e.target.value)} placeholder="e.g. John Doe" className={`${inputClass} ${errors.contactName ? "border-red-400" : "border-gray-200"}`} />
                      {errors.contactName && <p className="text-red-500 text-[11px] mt-1">{errors.contactName}</p>}
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Email <span className="text-red-400">*</span></label>
                      <input type="email" value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} placeholder="e.g. name@email.com" className={`${inputClass} ${errors.contactEmail ? "border-red-400" : "border-gray-200"}`} />
                      {errors.contactEmail && <p className="text-red-500 text-[11px] mt-1">{errors.contactEmail}</p>}
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact Phone <span className="text-gray-400">(optional)</span></label>
                      <input type="tel" value={form.contactNumber} onChange={(e) => update("contactNumber", e.target.value)} placeholder="e.g. (214) 555-0123" className={`${inputClass} border-gray-200`} />
                    </div>
                  </>
                )}

                {/* Photo */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Upload an Image *</label>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
                  {imagePreview ? (
                    <div className="flex items-center gap-3">
                      <img src={imagePreview} alt="Preview" className="w-20 h-14 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={removeImage} className="inline-flex items-center gap-1 text-[12px] text-red-500 hover:text-red-600 font-medium">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 hover:border-orange-300 hover:bg-orange-50/40 rounded-xl py-4 flex flex-col items-center justify-center transition-colors">
                      <ImageIcon className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-[12px] font-semibold text-gray-600">Add a photo</span>
                    </button>
                  )}
                </div>
                {errors.image && <p className="text-[11px] text-red-500 mt-1">{errors.image}</p>}

                {submitError && <div className="bg-red-50 border border-red-200 text-red-700 text-[12px] rounded-xl px-3.5 py-2.5">{submitError}</div>}

                {submitError && <div className="bg-red-50 border border-red-200 text-red-700 text-[12px] rounded-xl px-3.5 py-2.5">{submitError}</div>}

                <motion.button
                  type="submit"
                  disabled={submitting || (dealType === "event" && !selectedBusiness)}
                  whileHover={submitting || (dealType === "event" && !selectedBusiness) ? undefined : { scale: 1.02 }}
                  whileTap={submitting || (dealType === "event" && !selectedBusiness) ? undefined : { scale: 0.98 }}
                  className={`w-full font-bold text-[13.5px] py-3 rounded-xl transition-colors ${
                    dealType === "event" && !selectedBusiness
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-[#51ba4f] hover:bg-[#43a041] text-white"
                  } ${submitting ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {submitting ? "Posting…" : "Post Deal"}
                </motion.button>

              </form>
            </div>
          </aside>
          )}
        </div>
      </section>
    </div>
  );
}
